import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { cx } from '../../../shared/lib/cx.js';
import { formatVnd } from '../../../shared/lib/money.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import {
  confirmRecurring,
  restoreRecurring,
  skipRecurring,
  todayInTimeZone,
  undoRecurringConfirm,
  type RecurringTemplate,
  type TemplateOccurrence,
} from '../data/recurringRepository.js';
import { useTemplateHistory } from '../hooks/useTemplateHistory.js';
import { useTemplates } from '../hooks/useTemplates.js';
import { AMOUNT_MODE_LABELS, FREQUENCY_LABELS, dueDateLabel } from '../model/recurringLabels.js';
import listStyles from './RecurringPage.module.css';
import styles from './RecurringDetailPage.module.css';

const STATUS_LABELS: Record<TemplateOccurrence['status'], string> = {
  posted: 'Đã ghi',
  voided: 'Đã hoàn tác',
  skipped: 'Đã bỏ qua',
  pending: 'Chờ xác nhận',
  overdue: 'Quá hạn',
};

/** A year back and a year ahead: the whole recent life of the template, pending row included. */
function rangeAround(today: string): { from: string; to: string } {
  const year = Number(today.slice(0, 4));
  return { from: `${year - 1}${today.slice(4)}`, to: `${year + 1}${today.slice(4)}` };
}

/**
 * One recurring template and every occurrence it has had — posted, undone, skipped, waiting —
 * each with the correction that fits its state (decision AJ3, 2026-09-21).
 *
 * Hoàn tác (AE1), Bỏ qua (AE1) and Khôi phục (AJ1) all lived on /dinh-ky and Trang chủ before;
 * they are the same RPCs here, only placed next to the occurrence they act on. State is decided
 * by `app.recurring_template_history` in SQL; this screen formats and asks.
 */
export function RecurringDetailPage() {
  const { templateId } = useParams();
  const contextState = useCurrentContext();
  const templatesState = useTemplates();

  if (templateId === undefined) return <Navigate to="/dinh-ky" replace />;
  if (contextState.status === 'loading' || templatesState.status === 'loading') {
    return <LoadingState label="Đang tải khoản định kỳ…" />;
  }
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }
  if (templatesState.status === 'error') {
    return <ErrorState message={templatesState.message} onRetry={templatesState.retry} />;
  }

  const template = templatesState.templates.find((item) => item.id === templateId);
  if (template === undefined) {
    return (
      <EmptyState
        icon="🔍"
        title="Không tìm thấy khoản định kỳ"
        message="Khoản này không còn nữa hoặc không thuộc về bạn."
      >
        <Link to="/dinh-ky">Về danh sách khoản định kỳ</Link>
      </EmptyState>
    );
  }

  return (
    <TemplateDetail
      template={template}
      timeZone={contextState.context.household.timezone}
      onTemplateChanged={templatesState.refresh}
    />
  );
}

function TemplateDetail({
  template,
  timeZone,
  onTemplateChanged,
}: {
  template: RecurringTemplate;
  timeZone: string;
  onTemplateChanged: () => Promise<unknown>;
}) {
  const today = todayInTimeZone(timeZone);
  const { from, to } = rangeAround(today);
  const historyState = useTemplateHistory(template.id, from, to);

  const [busyDue, setBusyDue] = useState<string | null>(null);
  const [undoFor, setUndoFor] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const fixedAmount =
    template.amountMode === 'fixed'
      ? template.lines
          .filter((line) => line.side === 'debit')
          .reduce((sum, line) => sum + (line.amount ?? 0), 0)
      : null;

  /** Every action here is a confirmed write: run it, then reread the history and the template. */
  async function run(dueDate: string, action: () => Promise<unknown>, successMessage: string) {
    setBusyDue(dueDate);
    setMessage(null);
    setNotice(null);
    try {
      await action();
      setUndoFor(null);
      setNotice(successMessage);
      await Promise.all([
        historyState.status === 'ready' ? historyState.refresh() : Promise.resolve(),
        onTemplateChanged(),
      ]);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setBusyDue(null);
    }
  }

  return (
    <div className={styles.page}>
      <p className={styles.breadcrumb}>
        <Link to="/dinh-ky">← Khoản định kỳ</Link>
      </p>

      <header className={styles.hero}>
        <h1 className={styles.name}>{template.name}</h1>
        <p className={styles.meta}>
          {FREQUENCY_LABELS[template.frequency]} · {AMOUNT_MODE_LABELS[template.amountMode]}
          {fixedAmount !== null ? ` · ${formatVnd(fixedAmount)}` : ''}
          {template.isActive
            ? ` · kỳ tới ${dueDateLabel(template.nextDueDate, timeZone)}`
            : ' · đã tắt'}
        </p>
      </header>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <section className={lists.card} aria-label="Các kỳ">
        <h2 className={lists.cardTitle}>Các kỳ</h2>
        {historyState.status === 'loading' ? <LoadingState label="Đang tải các kỳ…" /> : null}
        {historyState.status === 'error' ? (
          <ErrorState message={historyState.message} onRetry={historyState.retry} />
        ) : null}
        {historyState.status === 'ready' && historyState.rows.length === 0 ? (
          <p className={forms.hint}>Chưa có kỳ nào trong một năm quanh hôm nay.</p>
        ) : null}
        {historyState.status === 'ready' ? (
          <ul className={lists.list}>
            {historyState.rows.map((row) => {
              const busy = busyDue === row.dueDate;
              return (
                <li key={`${row.dueDate}:${row.status}`}>
                  <div className={listStyles.row}>
                    <span className={lists.rowText}>
                      <span className={lists.rowName}>{dueDateLabel(row.dueDate, timeZone)}</span>
                      <span className={lists.rowMeta}>
                        <span
                          className={cx(
                            styles.status,
                            row.status === 'pending' && styles.statusPending,
                            row.status === 'overdue' && styles.statusOverdue,
                          )}
                        >
                          {STATUS_LABELS[row.status]}
                        </span>
                        {row.reason ? ` · ${row.reason}` : ''}
                      </span>
                    </span>

                    <span className={listStyles.rowRight}>
                      {row.amount !== null ? (
                        <span className={lists.amount}>{formatVnd(row.amount)}</span>
                      ) : fixedAmount !== null &&
                        (row.status === 'pending' || row.status === 'overdue') ? (
                        <span className={lists.amount}>{formatVnd(fixedAmount)}</span>
                      ) : null}

                      {row.status === 'posted' && row.entryId !== null ? (
                        <button
                          type="button"
                          className={listStyles.textButton}
                          disabled={busy}
                          aria-label={`Hoàn tác kỳ ${row.dueDate}`}
                          onClick={() => setUndoFor(undoFor === row.dueDate ? null : row.dueDate)}
                        >
                          Hoàn tác
                        </button>
                      ) : null}

                      {row.status === 'skipped' ? (
                        <button
                          type="button"
                          className={listStyles.textButton}
                          disabled={busy || !template.isActive}
                          aria-label={`Khôi phục kỳ ${row.dueDate}`}
                          onClick={() =>
                            void run(
                              row.dueDate,
                              () =>
                                restoreRecurring({
                                  templateId: template.id,
                                  dueDate: row.dueDate,
                                  reason: 'Khôi phục ở chi tiết khoản định kỳ',
                                }),
                              'Đã khôi phục — kỳ này hiện lại ở Đến hạn.',
                            )
                          }
                        >
                          Khôi phục
                        </button>
                      ) : null}

                      {row.status === 'pending' || row.status === 'overdue' ? (
                        <>
                          <button
                            type="button"
                            className={listStyles.textButton}
                            disabled={busy}
                            aria-label={`Bỏ qua kỳ ${row.dueDate}`}
                            onClick={() =>
                              void run(
                                row.dueDate,
                                () =>
                                  skipRecurring({
                                    templateId: template.id,
                                    dueDate: row.dueDate,
                                    reason: 'Bỏ qua ở chi tiết khoản định kỳ',
                                  }),
                                'Đã bỏ qua kỳ này — lịch chuyển sang kỳ sau.',
                              )
                            }
                          >
                            Bỏ qua
                          </button>
                          {/* An estimated template needs the real amount first; that prompt
                              lives on Đến hạn, so the row sends the person there. */}
                          {template.amountMode === 'fixed' ? (
                            <button
                              type="button"
                              className={forms.primary}
                              disabled={busy}
                              aria-label={`Xác nhận kỳ ${row.dueDate}`}
                              onClick={() =>
                                void run(
                                  row.dueDate,
                                  () =>
                                    confirmRecurring({
                                      templateId: template.id,
                                      dueDate: row.dueDate,
                                    }),
                                  'Đã ghi kỳ này.',
                                )
                              }
                            >
                              {busy ? 'Đang ghi…' : 'Xác nhận'}
                            </button>
                          ) : (
                            <Link to="/">Nhập số tiền ở Đến hạn</Link>
                          )}
                        </>
                      ) : null}
                    </span>
                  </div>

                  {undoFor === row.dueDate && row.entryId !== null ? (
                    <div className={listStyles.choiceActions}>
                      <button
                        type="button"
                        className={forms.secondary}
                        disabled={busy}
                        onClick={() =>
                          void run(
                            row.dueDate,
                            () =>
                              undoRecurringConfirm({
                                entryId: row.entryId!,
                                mode: 'skip',
                                reason: 'Hoàn tác ở chi tiết khoản định kỳ',
                              }),
                            'Đã hoàn tác — kỳ này coi như đã xong.',
                          )
                        }
                      >
                        Bỏ luôn kỳ này
                      </button>
                      <button
                        type="button"
                        className={forms.secondary}
                        disabled={busy}
                        onClick={() =>
                          void run(
                            row.dueDate,
                            () =>
                              undoRecurringConfirm({
                                entryId: row.entryId!,
                                mode: 'redo',
                                reason: 'Hoàn tác ở chi tiết khoản định kỳ',
                              }),
                            'Đã hoàn tác — kỳ này hiện lại để ghi đúng.',
                          )
                        }
                      >
                        Ghi lại sau
                      </button>
                      <button
                        type="button"
                        className={forms.secondary}
                        onClick={() => setUndoFor(null)}
                      >
                        Hủy
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
